import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import { push, ref, update, set, serverTimestamp } from 'firebase/database';
import { computed, observable, toJS } from 'mobx';
import { BaseFirebaseStore } from './BaseFirebaseStore';
import _ from 'lodash';
import { assert } from 'console';

export interface NarrativeItem {
    ui_displayIdGenerated: string;
    rootDocId: string;
    itemId: string;
    fullContextPath: string;
    level: NarrativeItemLevel;
    hypothesis: string;
    ui_display: string;
    sub_items?: NarrativeItem[];
}

export interface NarrativeNode {
    nodeId: string;
    hypothesis: string;
    ui_display: string;
    //parents? //?children
}

export interface TopologyNode {
    nodeId: string;
    children: TopologyNode[];
}

export type NarrativeItemLevel = 'L1' | 'L2' | 'L3' | null;

export interface SupportingFact {
    exact_fact_quote: string;
    logical_connection: string;
    //fact_citation: string;
    fact_citation_source_file: string;
    fact_citation_source_pages: string;
    fact_status: string;
    module_name: string;
    //claim: string;
    ui_title: string;
    hidden?: boolean;
}

export type FactsTable = {
    [entry_id: string]: {
        supporting_facts: SupportingFact[];
    };
};

export class dbPathsNarrative {
    constructor(private reportId: string) {}
    supportingFactsList(): string {
        return `narrative/${this.reportId}/facts_index`;
    }

    narrativeA1_Nodes(): string {
        return `narrative/${this.reportId}/narrative_a1/nodes`;
    }

    narrativeA1_NodeEntry(itemId: string): string {
        return `narrative/${this.reportId}/narrative_a1/nodes/${itemId}`;
    }
    narrativeA1_TopologyHistory(): string {
        return `narrative/${this.reportId}/narrative_a1/topology_history`;
    }

    narrativeA1_Topology(): string {
        return `narrative/${this.reportId}/narrative_a1/topology`;
    }

    narrativeA1(): string {
        return `narrative/${this.reportId}/narrative_a1`;
    }

    narrativeSpinnerState(): string {
        return `ui/show_narrative_page_spinner/${this.reportId}`;
    }
}

export class NarrativeStore extends BaseFirebaseStore {
    private parentsLookup: { [id: string]: NarrativeItem } = {};
    private itemById: { [id: string]: NarrativeItem } = {};
    private dbPaths: dbPathsNarrative;
    full_reset() {
        this.topologyNodeRoot = {
            nodeId: 'root',
            children: []
        };
        this.narrativeNodes = {};
        this.reset_lookups();
    }
    @observable accessor factsTable: FactsTable = {};
    @observable accessor isBackendWorking: boolean = false;

    @observable accessor narrativeNodes: { [nodeId: string]: NarrativeNode };
    @observable accessor topologyNodeRoot: TopologyNode;

    reset_lookups() {
        this.itemById = {};
        this.parentsLookup = {};
    }

    getNarrativeItemParent(itemId: string): NarrativeItem {
        return this.parentsLookup[itemId];
    }

    getNarrativeItemById(itemId: string): NarrativeItem | null {
        return this.itemById[itemId] ?? null;
    }

    @computed get narrativeTable(): NarrativeItem[] {
        if (
            !this.narrativeNodes ||
            Object.keys(this.narrativeNodes).length == 0
        ) {
            this.reset_lookups();
            return [];
        }

        if (
            !this.topologyNodeRoot ||
            !this.topologyNodeRoot.children ||
            this.topologyNodeRoot.children.length == 0
        ) {
            this.reset_lookups();
            return [];
        }

        const buildTreeRecursive = (
            node: TopologyNode,
            prefix_display_id: string,
            parentItemId: string,
            rootItemId: string,
            recursionTrack: number
        ): NarrativeItem[] => {
            //this.parentsLookup[itemId] = parent;
            //this.itemById[itemId] = items[i];

            if (recursionTrack > 5) {
                throw new Error(`buildTreeRecursive recursive-too deep!`);
            }
            let narrItems: NarrativeItem[] = [];
            let runningIndex = 1;
            //No need to take care of 'deleted' as they are removed from the list of topology

            if (!node.children) {
                return [];
            }

            for (let i = 0; i < node.children.length; i++) {
                let topology_item = node.children[i];
                let node_id = topology_item.nodeId;

                if (this.narrativeNodes?.[node_id] == null) {
                    console.error(`ERROR: Missing narrative node: ${node_id}`);
                    continue;
                }

                let node_item = this.narrativeNodes[node_id];
                let item_display_id = `${prefix_display_id}${runningIndex}.`;
                const item_level_lookup = {
                    0: 'L1',
                    1: 'L2',
                    2: 'L3'
                };

                const parentItem = this.itemById[parentItemId]; //Kinda sketchy
                this.parentsLookup[node_id] = parentItem;
                let contextPath = parentItem.fullContextPath;
                if (contextPath != '') {
                    contextPath += ':';
                }

                let new_item: NarrativeItem = {
                    rootDocId: recursionTrack == 0 ? node_id : rootItemId,
                    level: item_level_lookup[recursionTrack],
                    ui_displayIdGenerated: item_display_id,
                    itemId: node_id,
                    hypothesis: node_item.hypothesis,
                    ui_display: node_item.ui_display,
                    fullContextPath: `${contextPath}${node_id}`
                };

                this.itemById[node_id] = new_item;

                const rootIdToPass = recursionTrack == 0 ? node_id : rootItemId;
                new_item.sub_items = buildTreeRecursive(
                    topology_item,
                    item_display_id,
                    node_id,
                    rootIdToPass,
                    recursionTrack + 1
                );
                narrItems.push(new_item);
                runningIndex += 1;
            }

            return narrItems;
        };

        this.reset_lookups();
        //This is so the Tree can just start from 'root' node and no special cases for root\vs other
        const root_item: NarrativeItem = {
            level: 'L1',
            rootDocId: '',
            ui_displayIdGenerated: '',
            hypothesis: '',
            ui_display: '',
            fullContextPath: '',
            itemId: 'root',
            sub_items: []
        };
        this.itemById['root'] = root_item;

        const root_node_list = buildTreeRecursive(
            this.topologyNodeRoot,
            '',
            'root',
            'root',
            0
        );

        root_item.sub_items = root_node_list;
        return root_node_list;
    }

    constructor(private reportId: string) {
        super();
        this.dbPaths = new dbPathsNarrative(reportId);

        this.listenOnPath(this.dbPaths.supportingFactsList(), data => {
            this.factsTable = (data as FactsTable) ?? {};
        });

        this.listenOnPath(this.dbPaths.narrativeSpinnerState(), data => {
            this.isBackendWorking = data;
        });

        this.listenOnPath(this.dbPaths.narrativeA1_Nodes(), data => {
            this.narrativeNodes = data ?? {};
        });

        this.listenOnPath(this.dbPaths.narrativeA1_Topology(), data => {
            const make_sure_children_exists_recursive = (
                node: TopologyNode
            ) => {
                if (!node.children) {
                    node.children = [];
                }

                for (var child of node.children) {
                    make_sure_children_exists_recursive(child);
                }
            };
            const nodesTop: TopologyNode = data ?? {};
            make_sure_children_exists_recursive(nodesTop);
            this.topologyNodeRoot = nodesTop;
        });
    }
    pushTopologyHistory(change_message: string) {
        const fb = getFirebase();
        const topology = _.cloneDeep(toJS(this.topologyNodeRoot));
        const historyPath = this.dbPaths.narrativeA1_TopologyHistory();
        let r = ref(fb.db, historyPath);
        push(r, {
            time: serverTimestamp(),
            change_message,
            topology
        });
    }
    removeItemFromParent(narItem: NarrativeItem) {
        const parent = this.getNarrativeItemParent(narItem.itemId);
        if (parent) {
            const parentId = parent.itemId;

            const find_and_delete_recursive = (node: TopologyNode) => {
                if (node.nodeId == parentId) {
                    console.assert(
                        node.children.find(c => c.nodeId == narItem.itemId)
                    );

                    node.children = node.children.filter(
                        c => c.nodeId != narItem.itemId
                    );
                } else {
                    if (node.children) {
                        for (let child of node.children) {
                            find_and_delete_recursive(child);
                        }
                    }
                }
            };
            const fb = getFirebase();

            this.pushTopologyHistory(
                `Deleted ${narItem.itemId} / ${narItem.hypothesis}`
            );

            const topology = _.cloneDeep(toJS(this.topologyNodeRoot));

            find_and_delete_recursive(topology);

            const topologyPath = this.dbPaths.narrativeA1_Topology();
            let r = ref(fb.db, topologyPath);
            set(r, topology);

            const path = this.dbPaths.narrativeA1_NodeEntry(narItem.itemId);
            r = ref(fb.db, path);
            update(r, { deleted: 'true', delete_time: serverTimestamp() });
        } else {
            console.error('removeItemFromParent: Cant find parent');
        }
    }

    hideSupportingFact(itemId: string, factIndex: number) {
        const basePath = this.dbPaths.supportingFactsList();
        const path = `${basePath}/${itemId}/supporting_facts/${factIndex}`;

        const data = {
            hidden: true
        };

        const fb = getFirebase();
        let r = ref(fb.db, path);
        update(r, data);
    }

    editSupportingFact(
        itemId: string,
        factIndex: number,
        fact: SupportingFact
    ) {
        const basePath = this.dbPaths.supportingFactsList();
        const path = `${basePath}/${itemId}/supporting_facts/${factIndex}`;

        const fb = getFirebase();
        let r = ref(fb.db, path);
        update(r, fact);
    }

    editNarrativeItem(
        itemId: string,
        newHypothesis: string,
        newUiDisplay: string
    ) {
        const path = this.dbPaths.narrativeA1_NodeEntry(itemId);
        const updateData = {
            hypothesis: newHypothesis,
            ui_display: newUiDisplay
        };

        const fb = getFirebase();
        let r = ref(fb.db, path);
        update(r, updateData);
    }
}
